// src/pages/tour-packages/TourPackageEditPage.tsx

import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import { EditTourPackageForm } from "@/components/tour-packages/forms/EditTourPackageForm";
import { useTourPackageEditPage } from "./hooks/useTourPackageEditPage";

/* ----------------------
 * Breadcrumbs (same pattern)
 * ---------------------- */

function Breadcrumbs({ idLabel }: { idLabel?: string }) {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, idx) => {
    const href = "/" + parts.slice(0, idx + 1).join("/");
    const isNumericId = /^\d+$/.test(p);
    const label =
      isNumericId && idLabel
        ? idLabel
        : p
            .split("-")
            .map((s) => s[0]?.toUpperCase() + s.slice(1))
            .join(" ");
    return { href, label };
  });

  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Dashboard
          </Link>
          <span className="mx-1">/</span>
        </li>
        {crumbs.map((crumb, idx) => (
          <li key={crumb.href} className="flex items-center gap-1">
            {idx === crumbs.length - 1 ? (
              <span className="font-medium text-gray-700">{crumb.label}</span>
            ) : (
              <>
                <Link to={crumb.href} className="hover:text-gray-700">
                  {crumb.label}
                </Link>
                <span className="mx-1">/</span>
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ----------------------
 * Main page component
 * ---------------------- */

export default function TourPackageEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // ✅ validate id BEFORE calling the hook (hooks run even if you return early)
  const tourId = Number(id);
  const isValidId = Number.isFinite(tourId) && tourId > 0;

  // Call hook with a safe value; ideally your query hook uses `enabled: isValidId`
  const {
    form,
    handleSubmit,
    isSubmitting,
    citiesFieldArray,
    tourRatesFieldArray,
    itineraryDaysFieldArray,
    inclusionsFieldArray,
    exclusionsFieldArray,
    cancellationPoliciesFieldArray,
    hotelsFieldArray,
    imagesFieldArray,
    departuresFieldArray,
    tourTypes,
    suppliers,
    supplierLocked,
    isInitializing,
    initialLoadError,
    isSaving,
    saveError,
  } = useTourPackageEditPage(isValidId ? tourId : 0);

  // Show the tour name in the breadcrumb instead of the numeric id.
  const tourName = String(form.watch("tour.name") ?? "").trim() || undefined;

  // Invalid/missing id guard
  if (!isValidId) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <Breadcrumbs idLabel={tourName} />
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Invalid tour package ID.
          </div>
          <button
            type="button"
            onClick={() => navigate("/tour/tour-packages")}
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <FiArrowLeft className="h-3 w-3" />
            Back to tour packages
          </button>
        </div>
      </AdminLayout>
    );
  }

  // Initial loading – master data + existing tour
  if (isInitializing) {
    return (
      <AdminLayout>
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6">
          <Breadcrumbs idLabel={tourName} />
          <div className="mt-2 flex items-center justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-slate-900">
                Edit tour package
              </h1>
              <p className="text-xs text-slate-500">Loading tour details…</p>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-600">
            <div className="flex items-center gap-3">
              <span className="h-3 w-3 animate-spin rounded-full border border-slate-400 border-t-transparent" />
              Fetching tour package, tour types and suppliers…
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Initial error
  if (initialLoadError) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <Breadcrumbs idLabel={tourName} />
          <div className="mt-2 flex items-center justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-slate-900">
                Edit tour package
              </h1>
              <p className="text-xs text-slate-500">
                Unable to load this tour. Please try again.
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {(initialLoadError as any).message ||
              "An unexpected error occurred while loading the tour package."}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(0)}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => navigate("/tour/tour-packages")}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <FiArrowLeft className="h-3 w-3" />
              Back to list
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <Breadcrumbs idLabel={tourName} />

        {/* Header */}
        <div className="mt-2 flex items-center justify-between gap-2">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">
              Edit tour package
            </h1>
            <p className="text-xs text-slate-500">
              Update details, itinerary, rates and images.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/tour/tour-packages")}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <FiArrowLeft className="h-3 w-3" />
            Back to list
          </button>
        </div>

        {/* Top-level error (save/update) */}
        {saveError && (
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
            {(saveError as any).message ||
              "Failed to update tour package. Please check the form and try again."}
          </div>
        )}

        {/* Form */}
        <div className="mt-4">
          <EditTourPackageForm
            form={form}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting || isSaving}
            citiesFieldArray={citiesFieldArray}
            tourRatesFieldArray={tourRatesFieldArray}
            itineraryDaysFieldArray={itineraryDaysFieldArray}
            inclusionsFieldArray={inclusionsFieldArray}
            exclusionsFieldArray={exclusionsFieldArray}
            cancellationPoliciesFieldArray={cancellationPoliciesFieldArray}
            hotelsFieldArray={hotelsFieldArray}
            imagesFieldArray={imagesFieldArray}
            departuresFieldArray={departuresFieldArray}
            tourTypes={tourTypes}
            suppliers={suppliers}
            supplierLocked={supplierLocked}
            onCancel={() => navigate("/tour/tour-packages")}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
